import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function PrivacyPolicy() {
  return (
    <div className="container max-w-4xl py-12">
      <Helmet>
        <title>Privacy Policy - Trotting the World</title>
        <meta name="description" content="Our commitment to protecting your privacy and personal data." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <h1 className="text-4xl font-bold mb-6">Privacy Policy</h1>
      <p className="text-muted-foreground mb-8">
        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Introduction</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Trotting the World ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you visit our website. Please read this policy carefully to understand our 
              practices regarding your personal data.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Information We Collect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">1. Information You Provide</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Contact information (name, email) when you subscribe to our newsletter or contact us</li>
                <li>Comments and feedback you provide on our blog posts</li>
                <li>Any information you voluntarily provide through forms on our website</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">2. Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Page views, visit duration, and scroll depth</li>
                <li>Device information (browser type, operating system, screen resolution)</li>
                <li>IP address and approximate geographic location (country level)</li>
                <li>Referral sources and UTM parameters</li>
                <li>Browsing behavior and interaction with our content</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">3. Cookies and Tracking Technologies</h3>
              <p className="text-sm text-muted-foreground">
                We use cookies and similar tracking technologies to improve your experience. For detailed information about the cookies we use, 
                please refer to our{' '}
                <a href="/cookie-policy" className="text-primary hover:underline">Cookie Policy</a>.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>To provide, maintain, and improve our website and services</li>
              <li>To understand how visitors use our website and optimize user experience</li>
              <li>To send newsletters and promotional communications (with your consent)</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To display personalized advertisements (with your consent)</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations and enforce our terms of service</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Legal Basis for Processing (GDPR)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              If you are located in the European Economic Area (EEA), we process your personal data based on the following legal grounds:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li><strong>Consent</strong>: When you consent to analytics, advertising, or affiliate cookies</li>
              <li><strong>Legitimate Interest</strong>: To improve our website and provide relevant content</li>
              <li><strong>Legal Obligation</strong>: To comply with applicable laws and regulations</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Sharing and Third Parties</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              We may share your information with the following third parties:
            </p>
            
            <div>
              <h3 className="font-semibold mb-2">Advertising Partners (Two-Tier System)</h3>
              <p className="text-sm text-muted-foreground mb-2">
                We use Google AdSense to display advertisements. We offer you a choice:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground ml-4">
                <li>
                  <strong>With Consent (Personalized Ads)</strong>: If you accept advertising cookies, 
                  Google may use cookies and tracking technologies to serve personalized ads based on your 
                  browsing behavior across websites. This involves processing personal data.
                </li>
                <li>
                  <strong>Without Consent (Non-Personalized Ads)</strong>: If you decline advertising cookies, 
                  we serve non-personalized, contextual ads that are based only on the current page content 
                  and general location (e.g., country). These ads don't track your behavior or process 
                  personal data, making them GDPR-compliant without requiring consent.
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                You can change your advertising preferences at any time via our Cookie Settings. You can also 
                opt out of personalized advertising globally by visiting{' '}
                <a 
                  href="https://www.google.com/settings/ads" 
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google's Ad Settings
                </a>.{' '}
                Privacy Policy:{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Google Privacy Policy
                </a>
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Affiliate Partners</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li><strong>TravelPayouts</strong>: For affiliate marketing and tracking referrals</li>
                <li>Privacy Policy: <a href="https://www.travelpayouts.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TravelPayouts Privacy Policy</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Service Providers</h3>
              <p className="text-sm text-muted-foreground">
                We may use third-party service providers for hosting, email delivery, and analytics. These providers only have access to your 
                information as necessary to perform their functions and are obligated to maintain its confidentiality.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>International Data Transfers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Your information may be transferred to and processed in countries other than your country of residence, including the United States. 
              These countries may have data protection laws that differ from those in your country. We ensure appropriate safeguards are in place 
              to protect your personal data in compliance with GDPR and other applicable regulations.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Retention</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li><strong>Cookie Consent</strong>: 12 months from the date of consent</li>
              <li><strong>Analytics Data</strong>: 26 months or until you request deletion</li>
              <li><strong>Newsletter Subscriptions</strong>: Until you unsubscribe</li>
              <li><strong>Contact Inquiries</strong>: 2 years or until you request deletion</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Your Rights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li><strong>Right to Access</strong>: Request a copy of the personal data we hold about you</li>
              <li><strong>Right to Rectification</strong>: Request correction of inaccurate or incomplete data</li>
              <li><strong>Right to Erasure</strong>: Request deletion of your personal data ("right to be forgotten")</li>
              <li><strong>Right to Restriction</strong>: Request limitation of processing your data</li>
              <li><strong>Right to Data Portability</strong>: Receive your data in a structured, machine-readable format</li>
              <li><strong>Right to Object</strong>: Object to processing based on legitimate interests or direct marketing</li>
              <li><strong>Right to Withdraw Consent</strong>: Withdraw consent at any time without affecting prior processing</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-3">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:info@trottingtheworld.com" className="text-primary hover:underline">info@trottingtheworld.com</a>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Do Not Sell My Personal Information (CCPA)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              If you are a California resident, you have the right to opt-out of the "sale" of your personal information. While we do not 
              directly sell your personal data for monetary compensation, sharing data with advertising partners may be considered a "sale" 
              under California law. You can opt-out by disabling advertising cookies through our Cookie Preferences or by contacting us directly.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Children's Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Our website is not intended for children under 16 years of age. We do not knowingly collect personal information from children. 
              If you believe we have collected information from a child, please contact us immediately, and we will take steps to delete such information.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              We implement appropriate technical and organizational measures to protect your personal data from unauthorized access, disclosure, 
              alteration, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Changes to This Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you 
              of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of our 
              website after such changes constitutes acceptance of the updated policy.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="text-sm text-muted-foreground">
              <p><strong>Email:</strong> <a href="mailto:info@trottingtheworld.com" className="text-primary hover:underline">info@trottingtheworld.com</a></p>
              <p><strong>Website:</strong> <a href="https://trottingtheworld.com" className="text-primary hover:underline">https://trottingtheworld.com</a></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
